<script setup>
import { ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  // 是否显示
  visible: {
    type: Boolean,
    default: true,
  },
  // 外部传入的进度（0-100），不传则使用内置动画
  progress: {
    type: Number,
    default: null,
  },
  // 状态文字
  statusText: {
    type: String,
    default: '',
  },
  // 是否显示成功动画
  showSuccess: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:progress']);

// 内部进度
const internalProgress = ref(0);
let animationId = null;
let backendCompleted = false;
let phase2Starter = null;

// 显示的进度（优先使用外部传入的）
const displayProgress = ref(0);

// 监听外部进度
watch(
  () => props.progress,
  val => {
    if (val !== null) {
      displayProgress.value = val;
    }
  },
  { immediate: true }
);

// 监听内部进度
watch(internalProgress, val => {
  if (props.progress === null) {
    displayProgress.value = val;
  }
  emit('update:progress', displayProgress.value);
});

// 开始动画
const start = () => {
  internalProgress.value = 0;
  backendCompleted = false;

  const phase1Duration = 3000;
  const phase1Max = 90;
  const phase2Duration = 1000;

  // 阶段2: 90-100%
  const startPhase2 = () => {
    let phase2StartTime = performance.now();

    const animatePhase2 = currentTime => {
      const elapsed = currentTime - phase2StartTime;
      const timeRatio = Math.min(1, elapsed / phase2Duration);

      const baseProgress = 90 + timeRatio * 10;
      const sineWave = Math.sin(elapsed / 100) * 1.5;
      const randomFluctuation = (Math.random() - 0.5) * 1;
      const fluctuation = (sineWave + randomFluctuation) * (1 - timeRatio);

      internalProgress.value = Math.min(
        100,
        Math.max(90, baseProgress + fluctuation)
      );

      if (elapsed >= phase2Duration) {
        internalProgress.value = 100;
        animationId = null;
        return;
      }

      animationId = requestAnimationFrame(animatePhase2);
    };

    animationId = requestAnimationFrame(animatePhase2);
  };

  phase2Starter = startPhase2;

  // 阶段1: 0-90%
  let phase1StartTime = performance.now();

  const animatePhase1 = currentTime => {
    const elapsed = currentTime - phase1StartTime;
    const timeRatio = Math.min(1, elapsed / phase1Duration);

    const baseProgress = timeRatio * phase1Max;
    const sineWave = Math.sin(elapsed / 150) * 4;
    const randomFluctuation = (Math.random() - 0.5) * 3;
    const fluctuation = (sineWave + randomFluctuation) * (1 - timeRatio * 0.7);

    internalProgress.value = Math.min(
      phase1Max,
      Math.max(0, baseProgress + fluctuation)
    );

    if (elapsed >= phase1Duration) {
      internalProgress.value = phase1Max;

      if (backendCompleted) {
        startPhase2();
      } else {
        animationId = null;
      }
      return;
    }

    animationId = requestAnimationFrame(animatePhase1);
  };

  animationId = requestAnimationFrame(animatePhase1);
};

// 完成动画（后端返回成功后调用）
const complete = () => {
  return new Promise(resolve => {
    backendCompleted = true;

    if (!animationId && phase2Starter) {
      phase2Starter();
    }

    // 等待动画完成
    const checkComplete = () => {
      if (internalProgress.value >= 100) {
        resolve();
      } else {
        requestAnimationFrame(checkComplete);
      }
    };

    if (animationId) {
      checkComplete();
    } else {
      resolve();
    }
  });
};

// 停止动画
const stop = () => {
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }
};

// 暴露方法
defineExpose({
  start,
  complete,
  stop,
});

onUnmounted(() => {
  stop();
});
</script>

<template>
  <div v-if="visible" class="mt-3">
    <!-- 进度条容器 -->
    <div
      class="relative h-3 bg-n-slate-3 dark:bg-n-slate-6 rounded-full overflow-hidden"
    >
      <!-- 背景渐变流动 -->
      <div class="absolute inset-0 animate-gradient-flow" />

      <!-- 进度条主体 -->
      <div
        class="h-full rounded-full relative overflow-hidden transition-all duration-100 ease-out"
        :class="{
          'animate-pulse-fast': displayProgress >= 100,
        }"
        :style="{
          width: `${displayProgress}%`,
          background:
            'linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e, #06b6d4, #3b82f6, #8b5cf6)',
        }"
      >
        <!-- 进度条内闪光效果 -->
        <div class="absolute inset-0 animate-shimmer" />
      </div>

      <!-- 完成时的脉冲效果 -->
      <div
        v-if="showSuccess"
        class="absolute inset-0 bg-n-emerald-500/30 rounded-full animate-ping"
      />
    </div>

    <!-- 状态文字和百分比 -->
    <div v-if="statusText" class="flex items-center justify-between mt-1.5">
      <p class="text-xs text-n-slate-10 flex items-center gap-1.5">
        <span
          v-if="displayProgress < 100"
          class="inline-block w-1.5 h-1.5 bg-n-blue-500 rounded-full animate-pulse"
        />
        <span v-else class="i-lucide-check text-n-emerald-500 text-xs" />
        {{ statusText }}
      </p>
      <p class="text-xs font-medium text-n-slate-11">
        {{ Math.round(displayProgress) }}%
      </p>
    </div>
  </div>
</template>

<style scoped>
/* 进度条闪光动画 */
@keyframes shimmer {
  0% {
    transform: translateX(-150%) skewX(-15deg);
  }
  100% {
    transform: translateX(300%) skewX(-15deg);
  }
}

.animate-shimmer {
  animation: shimmer 1s ease-in-out infinite;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0) 20%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 0) 80%,
    transparent 100%
  );
  background-size: 200% 100%;
  width: 60%;
}

/* 背景渐变流动 */
@keyframes gradient-flow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.animate-gradient-flow {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 25%,
    rgba(255, 255, 255, 0.25) 50%,
    rgba(255, 255, 255, 0.15) 75%,
    rgba(255, 255, 255, 0) 100%
  );
  background-size: 200% 100%;
  animation: gradient-flow 3s ease infinite;
}

/* 快速脉冲动画 */
@keyframes pulse-fast {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.01);
  }
}

.animate-pulse-fast {
  animation: pulse-fast 0.3s ease-in-out 3;
}
</style>
